import React from 'react';
import { ClearableSelect } from '../clearable-select';

interface AccountTypeSelectProps {
  /**
   * The list of available account types to display
   * @required
   * @type {string[]}
   */
  accountTypes: string[];
  /**
   * The currently selected account type
   * @optional
   * @type {string}
   */
  selectedAccountType?: string | null;
  /**
   * Callback fired when the account type selection changes
   * @required
   * @type {(accountType: string | null) => void}
   */
  onAccountTypeChange: (accountType: string | null) => void;
}

/**
 * Select component for choosing an account type filter.
 * Allows users to filter organizations by their account type.
 *
 * @param props - The component props
 * @param props.accountTypes - Available account types to choose from
 * @param props.selectedAccountType - The currently selected account type
 * @param props.onAccountTypeChange - Callback fired when the account type changes
 */
function AccountTypeSelect(props: AccountTypeSelectProps) {
  const { accountTypes, selectedAccountType, onAccountTypeChange } = props;

  const selectedKey = React.useMemo(() => (selectedAccountType ? selectedAccountType : null), [selectedAccountType]);

  const items = React.useMemo(() => accountTypes.map((type) => ({ id: type, label: type })), [accountTypes]);

  return (
    <ClearableSelect
      ariaLabel="Account type"
      placeholder="All account types"
      items={items}
      selectedKey={selectedKey}
      onSelectionChange={onAccountTypeChange}
      contentClassName="max-h-[500px] overflow-y-auto"
    />
  );
}

export { AccountTypeSelect };
export type { AccountTypeSelectProps };
